/**
 * Composition root: opens the store, builds the scene and both presentation modes, and
 * wires the UI to them. Deliberately holds no logic of its own worth testing.
 */

import { VRButton } from 'three/addons/webxr/VRButton.js';

import { resolveApiBaseUrl } from './api/apiBaseUrl.js';
import { AuthApi } from './api/authApi.js';
import { colourOf } from './domain/colour.js';
import { boundsAgree } from './domain/bounds.js';
import { boundsSize } from './domain/scaling.js';
import { colourCheckPending, hasFileColours, isHeavy } from './domain/modelFile.js';
import { roomScaleOf } from './domain/nudge.js';
import { readPlyColours } from './domain/ply.js';
import { logger } from './log.js';
import { readModelFile } from './readModel.js';
import type { Bounds, ModelId, ModelMeta } from './domain/types.js';
import {
  estimateUsage,
  ModelStore,
  requestPersistence,
  StorageFullError,
} from './storage/modelStore.js';
import { describeContents, formatBytes, formatDimensions } from './format.js';
import { Sharing } from './share/sharing.js';
import { AboutPanel } from './ui/aboutPanel.js';
import { AuthPanel } from './ui/authPanel.js';
import { ColourPicker } from './ui/colourPicker.js';
import { buttonElement, element, inputElement } from './ui/dom.js';
import { LibraryPanel } from './ui/libraryPanel.js';
import { NoticeBoard } from './ui/noticeBoard.js';
import { SharePanel } from './ui/sharePanel.js';
import { SidebarToggle } from './ui/sidebarToggle.js';
import { ThemeToggle } from './ui/themeToggle.js';
import { Thumbnails } from './ui/thumbnails.js';
import { createUploadZone } from './ui/uploadZone.js';
import { DesktopMode } from './viewer/desktopMode.js';
import { SceneManager, type SceneModel } from './viewer/sceneManager.js';
import { VrMode } from './viewer/vrMode.js';

const LAST_SELECTED_KEY = '3dpreview:last-selected';

const accountLog = logger('accounts');

/**
 * A model built and in the scene, with the record that describes it.
 *
 * The record comes back as well as the geometry because loading can settle a fact about
 * the file that the caller's copy predates — whether it carries colours of its own — and
 * a caller acting on the stale copy would offer to recolour a model that owns its colours.
 */
interface Loaded {
  readonly model: SceneModel;
  readonly meta: ModelMeta;
}

async function start(): Promise<void> {
  const canvasHost = element('canvas-host');
  const stageStatus = element('stage-status');
  const quota = element('library-quota');
  const gauge = element('library-gauge');
  const notices = new NoticeBoard();

  let models: ModelMeta[] = [];
  let selectedId: ModelId | undefined;
  /** Who is signed in, so models added now can be attributed and later shared. */
  let accountId: string | undefined;

  // Built early so the header button works from the first moment the page is up, rather
  // than only once the library has been read. Whether it shows itself unprompted is
  // decided at the end of this function, where the size of the library is known.
  const about = new AboutPanel();

  const scene = new SceneManager(canvasHost);
  const desktop = new DesktopMode(scene);

  // Set up before the first frame is drawn, so the canvas is never briefly the wrong
  // colour. Applies the stored theme as it is constructed, which is what paints it.
  new ThemeToggle({
    button: buttonElement('theme-button'),
    onThemeChanged: (theme) => {
      // On a screen the canvas takes the stage's own colour, so the two are one surface.
      // The room in VR follows the same theme but has its own pair of looks.
      scene.setPageBackground(stageBackground());
      scene.setRoomTheme(theme);
    },
  });

  // Likewise before the first frame: it applies the stored state as it is constructed, and
  // that is what decides how wide the canvas starts out. The `ResizeObserver` further down
  // is what tells the renderer about it, then and every time afterwards.
  //
  // Folding the library is also what sets its pictures being taken, so the answer is read
  // back below once the thing that takes them exists. It cannot be handed one here: this
  // runs before there is a store to read models out of.
  const sidebar = new SidebarToggle({
    button: buttonElement('library-collapse'),
    onChange: (collapsed) => {
      thumbnails.setActive(collapsed);
      renderLibrary();
    },
  });

  // Deleting is the same operation the sidebar performs, so it is wired to the same
  // function. Putting a model in the room has no equivalent on the desktop, where one
  // model is shown at a time — see `DesktopMode` for why.
  const vr = new VrMode(scene, {
    onToggle: (id) => {
      void toggleInRoom(id);
    },
    onDelete: (id) => {
      const meta = models.find((candidate) => candidate.id === id);
      if (meta) void deleteModel(meta);
    },
    onExit: () => {
      void scene.renderer.xr.getSession()?.end();
    },
    onResize: (id, roomScale) => {
      void saveRoomScale(id, roomScale);
    },
  });

  scene.setMode(desktop);
  scene.start();

  const store = await ModelStore.open();
  void requestPersistence();

  /**
   * Report something that went wrong.
   *
   * These used to be one line that the next action overwrote, which meant a failure could
   * vanish before it had been read. Now each stands on its own until it is dismissed or
   * until it has been up long enough to have been read — a span the board works out from
   * how much there is to read, and stops counting while somebody is reading it. Repeats of
   * the same complaint collapse into the one already showing rather than piling up.
   */
  const showError = (text: string, detail?: string): void => {
    notices.error(text, detail);
  };

  const setStatus = (meta: ModelMeta | undefined): void => {
    if (!meta) {
      stageStatus.textContent = models.length > 0 ? 'Choose a model to view it.' : '';
      return;
    }

    const parts = [
      meta.name,
      formatDimensions(boundsSize(meta.bounds)),
      describeContents(meta),
    ];
    if (isHeavy(meta)) {
      parts.push('— large, may render slowly in VR');
    }
    stageStatus.textContent = parts.join(' · ');
  };

  const refreshQuota = async (): Promise<void> => {
    const usage = await estimateUsage();
    if (!usage) {
      quota.textContent = '';
      gauge.hidden = true;
      return;
    }

    quota.textContent =
      usage.quotaBytes === undefined
        ? `${formatBytes(usage.usedBytes)} stored`
        : `${formatBytes(usage.usedBytes)} of ${formatBytes(usage.quotaBytes)} used`;

    // The same figure as a fraction, for the square pie the folded sidebar shows in place
    // of the sentence. A browser that will not say what the quota is leaves nothing to
    // take a fraction of, so there is no pie to draw — the sentence still says how much is
    // stored, which is all that is known.
    gauge.hidden = usage.quotaBytes === undefined;
    gauge.title = quota.textContent;
    if (usage.quotaBytes !== undefined && usage.quotaBytes > 0) {
      gauge.style.setProperty(
        '--used',
        sweep(usage.usedBytes / usage.quotaBytes).toFixed(4),
      );
    }
  };

  const colours = new ColourPicker({
    // Repainting is immediate and costs nothing; saving waits until the choice settles.
    // The picker only ever acts on the selection, which is the one model on screen.
    onPreview: (colour) => {
      if (selectedId !== undefined) scene.setModelColour(selectedId, colour);
    },
    onCommit: (id, colour) => {
      void saveColour(id, colour);
    },
  });

  // Declared before the panel that asks it for pictures and after nothing, because it
  // reports back by asking for the sidebar to be drawn again — which is a function defined
  // below both. `renderLibrary` is hoisted; `panel` is not, hence the order.
  const thumbnails = new Thumbnails({
    scene,
    store,
    onReady: () => {
      renderLibrary();
    },
  });

  // The state the toggle came up in, which it could not tell anybody about at the time.
  thumbnails.setActive(sidebar.collapsed);

  const panel = new LibraryPanel({
    list: element('library-list'),
    empty: element('library-empty'),
    notice: element('library-notice'),
    rail: element('library-rail'),
    thumbnailFor: (meta) => thumbnails.urlFor(meta),
    onSelect: (id) => {
      void selectModel(id);
    },
    onDelete: (meta) => {
      void deleteModel(meta);
    },
  });

  // Both views of the library are refreshed together, so the VR menu never shows a model
  // that has been deleted or misses one that has been added.
  function renderLibrary(): void {
    panel.render(models, selectedId);
    vr.setLibrary(models);
  }

  /**
   * Build a model's geometry, if it is not already built.
   *
   * Parsing is the expensive part and the result is shared: a model that is both the
   * desktop selection and standing in the room is one set of geometry with one root,
   * shown by whichever mode is active.
   */
  async function ensureLoaded(meta: ModelMeta): Promise<Loaded | undefined> {
    const existing = scene.get(meta.id);
    if (existing) return { model: existing, meta };

    const blob = await store.getFile(meta.id);
    if (!blob) {
      showError(`The file for ${meta.name} is missing from storage.`);
      return undefined;
    }

    const data = await blob.arrayBuffer();
    // The record may predate the field that says whether the file brings its own colours,
    // and this is the one moment its bytes are in hand anyway. Returned as well as stored,
    // because the caller is about to show a colour control that depends on the answer.
    const current = await checkFileColours(meta, data);

    try {
      const model = await scene.load(current.id, data, current, colourOf(current));
      return { model, meta: await checkBounds(current, model.bounds) };
    } catch (error) {
      showError(`${meta.name} could not be rendered.`);
      console.error(error);
      return undefined;
    }
  }

  /**
   * Correct a record whose measurements no longer match what its file builds.
   *
   * Normally they agree exactly, having been taken from the same bytes. They come apart
   * when the rule for reading a format changes — reading a glTF's units as metres rather
   * than as millimetres moves every dimension by a factor of a thousand — and a record
   * left behind then describes a model that no longer exists, in the library and anywhere
   * else that reads it.
   *
   * The renderer already places the model from what it built, so this is not what keeps
   * the view correct. It is what stops the list beside it disagreeing.
   */
  async function checkBounds(meta: ModelMeta, measured: Bounds): Promise<ModelMeta> {
    if (boundsAgree(meta.bounds, measured)) return meta;

    let updated: ModelMeta = { ...meta, bounds: measured };
    try {
      updated = (await store.setBounds(meta.id, measured)) ?? updated;
    } catch (error) {
      console.error(error);
    }

    models = models.map((candidate) => (candidate.id === meta.id ? updated : candidate));
    renderLibrary();
    return updated;
  }

  /**
   * Work out whether a stored file has colours of its own, if nothing has yet.
   *
   * Only ever does anything once per model: the answer is written back, including when it
   * is "no", so a PLY without colours is not re-examined on every display.
   *
   * A failed write is not a failed load. The flag is a record of something we can work out
   * again from the file, so a full disk costs a repeated check rather than a model that
   * will not open.
   */
  async function checkFileColours(
    meta: ModelMeta,
    data: ArrayBuffer,
  ): Promise<ModelMeta> {
    if (!colourCheckPending(meta)) return meta;

    const fileColours = readPlyColours(data);
    let updated: ModelMeta = { ...meta, fileColours };

    try {
      updated = (await store.setFileColours(meta.id, fileColours)) ?? updated;
    } catch (error) {
      console.error(error);
    }

    models = models.map((candidate) => (candidate.id === meta.id ? updated : candidate));
    return updated;
  }

  /**
   * Free a model that nothing is showing any more.
   *
   * A model earns its place in memory by being either the selection or in the room. A
   * headset holding several large models at once is exactly where this matters: without
   * it, every model ever glanced at would stay parsed for the life of the page.
   */
  const releaseIfUnused = (id: ModelId): void => {
    if (id === selectedId || vr.isInRoom(id)) return;
    scene.unload(id);
  };

  /**
   * Put a model in the room, or take it out again — the in-VR menu's row action.
   *
   * Does not touch the selection. The two are separate ideas: the selection is what the
   * desktop shows, and while somebody is wearing a headset it is not something they can
   * see or act on, so changing it from in here would be an invisible side effect.
   */
  async function toggleInRoom(id: ModelId): Promise<void> {
    if (vr.isInRoom(id)) {
      vr.removeFromRoom(id);
      releaseIfUnused(id);
      renderLibrary();
      return;
    }

    const meta = models.find((candidate) => candidate.id === id);
    if (!meta) return;

    const loaded = await ensureLoaded(meta);
    if (!loaded) return;

    vr.addToRoom(loaded.model, roomScaleOf(loaded.meta));
    renderLibrary();
  }

  async function selectModel(id: ModelId): Promise<void> {
    const meta = models.find((candidate) => candidate.id === id);
    if (!meta) return;

    stageStatus.textContent = `Loading ${meta.name}…`;

    const loaded = await ensureLoaded(meta);
    if (!loaded) {
      stageStatus.textContent = '';
      return;
    }

    const previous = selectedId;
    selectedId = id;
    localStorage.setItem(LAST_SELECTED_KEY, id);

    // After the change, so a model that was only being kept for the selection is freed
    // now that it has stopped being it — unless it is standing in the room.
    if (previous !== undefined && previous !== id) releaseIfUnused(previous);

    desktop.setModel(loaded.model);
    // `loaded.meta`, not `meta`: loading may have settled whether this file carries its
    // own colours, which is exactly what decides whether the picker is offered.
    colours.showFor(loaded.meta);
    renderLibrary();
    setStatus(loaded.meta);
  }

  /**
   * Record the colour chosen for a model.
   *
   * The record is re-read from the store rather than patched in memory, because the write
   * may have landed after the model was deleted — in which case there is nothing to
   * update and nothing to show.
   */
  async function saveColour(id: ModelId, colour: string): Promise<void> {
    try {
      const updated = await store.setColour(id, colour);
      if (!updated) return;

      models = models.map((candidate) => (candidate.id === id ? updated : candidate));
      renderLibrary();
    } catch (error) {
      showError(
        error instanceof StorageFullError
          ? error.message
          : 'That colour could not be saved.',
      );
      if (!(error instanceof StorageFullError)) console.error(error);
    }
  }

  /**
   * Record how large a model was left standing in the room.
   *
   * Same shape as `saveColour`, and for the same reasons — the record is re-read rather
   * than patched, since the resize may have finished after the model was deleted.
   *
   * The failure is reported to the console alone. It arrives while the user is wearing a
   * headset, where the notice board is not visible and there is nothing they could do
   * about it; the model goes on being the size they made it for as long as the page lives,
   * and the only loss is that it will not be that size next time.
   */
  async function saveRoomScale(id: ModelId, roomScale: number): Promise<void> {
    try {
      const updated = await store.setRoomScale(id, roomScale);
      if (!updated) return;

      models = models.map((candidate) => (candidate.id === id ? updated : candidate));
      renderLibrary();
    } catch (error) {
      console.error('The size this model was left at could not be saved.', error);
    }
  }

  /** Stop displaying whatever is displayed, because it is no longer there to display. */
  const forgetSelection = (): void => {
    const previous = selectedId;
    selectedId = undefined;
    localStorage.removeItem(LAST_SELECTED_KEY);

    desktop.setModel(undefined);
    if (previous !== undefined) releaseIfUnused(previous);

    colours.showFor(undefined);
    setStatus(undefined);
  };

  /**
   * Re-read the library from storage and redraw both views of it.
   *
   * Needed because sharing changes the library from outside this file: models arrive from
   * another session, and deletions made elsewhere arrive with them.
   */
  const reloadLibrary = async (): Promise<void> => {
    models = await store.list();
    renderLibrary();
    void refreshQuota();
  };

  /**
   * Take on a colour or a size that another session decided, for models on show here.
   *
   * The store has already written them; this is the part the list refresh cannot do,
   * because a model on the stage or standing in the room is geometry that was built from
   * the record as it was and does not re-read it.
   *
   * Records are re-read rather than taken from the amendment: what the store settled on may
   * not be what arrived, since it compares timestamps again and keeps a local decision that
   * turns out to be the later one.
   */
  async function restyle(ids: readonly ModelId[]): Promise<void> {
    const changed = new Set(ids);
    models = await store.list();

    for (const meta of models) {
      if (!changed.has(meta.id) || !scene.has(meta.id)) continue;

      // A file that brings its own colours is never given one, here or anywhere else, so
      // there is nothing an amendment could be asking for.
      if (!hasFileColours(meta)) scene.setModelColour(meta.id, colourOf(meta));
      vr.resizeInRoom(meta.id, roomScaleOf(meta));
    }

    renderLibrary();
  }

  async function deleteModel(meta: ModelMeta): Promise<void> {
    await store.delete(meta.id);
    models = models.filter((candidate) => candidate.id !== meta.id);

    // Out of the room first: a model deleted from the library cannot go on standing in
    // it, and the room is what would otherwise keep its geometry loaded for ever.
    vr.removeFromRoom(meta.id);
    if (selectedId === meta.id) forgetSelection();
    scene.unload(meta.id);
    thumbnails.forget(meta.id);

    renderLibrary();
    void refreshQuota();
  }

  async function addFiles(files: readonly File[]): Promise<void> {
    const problems: string[] = [];
    let lastAdded: ModelMeta | undefined;

    for (const file of files) {
      const bytes = await file.arrayBuffer();
      const result = await readModelFile(bytes);

      if (!result.ok) {
        problems.push(`${file.name}: ${result.message}`);
        continue;
      }

      try {
        // The bytes are handed on so the store can hash them without reading the file a
        // second time; the account, so that this model can later be shared.
        lastAdded = await store.add(file.name, file, result.stats, {
          bytes,
          ...(accountId === undefined ? {} : { owner: accountId }),
        });
        models = [lastAdded, ...models];
      } catch (error) {
        problems.push(
          error instanceof StorageFullError
            ? `${file.name}: ${error.message}`
            : `${file.name} could not be saved.`,
        );
        if (!(error instanceof StorageFullError)) console.error(error);
      }
    }

    if (problems.length > 0) showError(problems.join(' '));

    renderLibrary();
    void refreshQuota();

    // Jump straight to the newest upload — adding a file is a request to look at it.
    if (lastAdded) await selectModel(lastAdded.id);
  }

  createUploadZone({
    root: element('upload-zone'),
    input: inputElement('upload-input'),
    onFiles: (files) => {
      void addFiles(files);
    },
  });

  element('reset-view').addEventListener('click', () => {
    if (scene.renderer.xr.isPresenting) {
      vr.resetPlacement();
    } else {
      desktop.resetView();
    }
  });

  // Entering and leaving VR is the mode switch; nothing else triggers it.
  scene.renderer.xr.addEventListener('sessionstart', () => {
    // Put the selection out on the first visit, so a headset does not open on an empty
    // room with the menu closed — which looks like a broken viewer rather than an
    // invitation. On later visits the room is whatever the user last arranged.
    const selected = selectedId === undefined ? undefined : scene.get(selectedId);
    if (selected && vr.isRoomEmpty) vr.addToRoom(selected);

    scene.setMode(vr);
    renderLibrary();
  });
  scene.renderer.xr.addEventListener('sessionend', () => {
    scene.setMode(desktop);
  });

  new ResizeObserver(() => {
    scene.resize();
    desktop.handleResize();
  }).observe(canvasHost);

  // A development-only handle on the internals. Stripped from production builds.
  //
  // VR mode is otherwise unreachable without a headset, and the Quest browser's remote
  // debugging is awkward enough that being able to poke the scene from a console is
  // worth the four lines. See the README for the inspection recipes.
  if (import.meta.env.DEV) {
    // `toggleInRoom` among them because putting a model in the room is otherwise only
    // reachable by pointing a controller at the menu, which is exactly the thing that
    // cannot be done from a desk.
    Object.assign(window, { viewer: { scene, desktop, vr, store, toggleInRoom } });
  }

  // Added unconditionally. Where VR is unavailable three renders this as an explanatory
  // "VR not supported" notice, which tells a desktop visitor the mode exists and that
  // their browser cannot reach it — more use than an empty corner.
  const vrHost = element('vr-button-host');
  vrHost.append(VRButton.createButton(scene.renderer));

  // Whether that notice is all it will ever be, recorded where the stylesheet can read it.
  // On a phone the sentence is worth nothing — a phone is not going to grow a headset —
  // and it is a whole line of a screen that has few to spare, so the narrow layout drops
  // it. three answers the same question for itself and keeps the answer to itself: the
  // button it builds is left enabled and merely relabelled, so there is no state on it for
  // a selector to match. Asking again costs one call and is the only way to know.
  void immersiveAvailable().then((available) => {
    vrHost.dataset.vr = available ? 'ready' : 'unavailable';
  });

  // Accounts are optional and additive: the library above is stored in this browser and
  // works without any of this. A build with no API configured skips it entirely, and one
  // whose API is unreachable hides the button and carries on.
  const apiBaseUrl = resolveApiBaseUrl(
    import.meta.env.VITE_API_BASE_URL,
    import.meta.env.DEV,
  );

  // Said out loud because the two outcomes are indistinguishable on the page — no button
  // either way — and only one of them is a mistake. Somebody wondering where the sign-in
  // button went needs to know first whether one was ever going to appear.
  if (apiBaseUrl === undefined) {
    accountLog.info(
      'No account service is configured, so this viewer runs entirely on this device. ' +
        'Set VITE_API_BASE_URL at build time to enable accounts and sharing.',
    );
  } else {
    accountLog.info(`The account service is expected at ${apiBaseUrl}.`);
  }

  if (apiBaseUrl !== undefined) {
    const api = new AuthApi(apiBaseUrl);

    // Sharing needs an account to say which sessions belong together, so it is built here
    // alongside the accounts it depends on and does not exist at all without them.
    const sharing = new Sharing({
      store,
      apiBaseUrl,
      csrfToken: () => api.currentCsrfToken(),
      onPresence: (others) => {
        sharePanel.setOtherSessions(others);
      },
      onHealth: (health) => {
        sharePanel.setHealth(health);
      },
      onLibraryChanged: () => {
        void reloadLibrary();
      },
      onModelsRemoved: (ids) => {
        // Models deleted on another device, arriving while they are on screen here.
        //
        // Out of the room as well as off the stage: a model that is no longer in the
        // library is no longer in the menu either, so left standing there it could not be
        // taken out — there would be no row left to tap.
        for (const id of ids) {
          vr.removeFromRoom(id);
          if (id === selectedId) forgetSelection();
          scene.unload(id);
        }
      },
      onModelsRevised: (ids) => {
        void restyle(ids);
      },
    });

    const sharePanel = new SharePanel({
      sharing,
      store,
      isPresenting: () => scene.renderer.xr.isPresenting,
      onNotice: (notice) => {
        notices.show(notice);
      },
    });

    const authPanel = new AuthPanel({
      api,
      apiBaseUrl,
      // The headset cannot sign in or out, but it can say which it is, and a user who
      // cannot see the header has no other way to find out.
      onAccountChange: (account) => {
        vr.setAccount(account?.email);
        accountId = account?.id;
        sharing.setAccount(account?.id);
        sharePanel.setAvailable(account !== undefined);
        // Signing out takes the indicator with the button. Signing in leaves it away
        // until the connection has something to report, rather than showing a colour that
        // is only going to be replaced a second later.
        if (account === undefined) sharePanel.setHealth(undefined);
      },
    });

    // The share button is not offered in VR — see the note in `share/sharing.ts` for why
    // answering a share still is.
    scene.renderer.xr.addEventListener('sessionstart', () => {
      sharePanel.handlePresentationChange();
    });
    scene.renderer.xr.addEventListener('sessionend', () => {
      sharePanel.handlePresentationChange();
    });

    // The panel is built before any of this, because its button has to work from the
    // first moment the page is up. Which build the service is running is something only
    // this half of the composition knows how to ask.
    about.watchService(() => api.serviceBuild());

    // Not awaited: a slow or unresponsive account service must not hold up the viewer.
    void authPanel.start();
  }

  models = await store.list();
  renderLibrary();
  void refreshQuota();

  // A visitor with nothing in their library has nothing to look at, so the welcome has
  // the screen to itself; one who came back for a model they saved is here for that model
  // and is not interrupted. The panel decides for itself whether it has already been sent
  // away for good.
  about.offer(models.length);

  const remembered = localStorage.getItem(LAST_SELECTED_KEY);
  if (remembered !== null && models.some((meta) => meta.id === remembered)) {
    await selectModel(remembered);
  } else {
    setStatus(undefined);
  }
}

/**
 * Smallest wedge the storage gauge will draw for a library that is using anything at all.
 *
 * A browser offers gigabytes and a library of a dozen models is a few megabytes, so the
 * honest figure is a fraction of a percent and the honest picture is an empty square —
 * which reads as a gauge that has failed rather than as one with almost nothing in it.
 * A sliver says "in use, barely", which is the true thing the geometry cannot otherwise
 * express. The sentence beside it, and the tooltip when the sentence is folded away, carry
 * the actual figures.
 */
const MIN_GAUGE_SWEEP = 0.03;

/** The fraction to draw for a measured one: floored if it is small, capped at full. */
function sweep(fraction: number): number {
  if (!(fraction > 0)) return 0;
  return Math.min(1, Math.max(MIN_GAUGE_SWEEP, fraction));
}

/**
 * Whether this browser can actually enter an immersive session.
 *
 * Only used to decide whether the VR button deserves a line of a small screen, so every
 * uncertain answer is "no": a browser with no `navigator.xr` at all, and one that has the
 * API but throws when asked — some refuse the question outright inside an iframe, or where
 * a permissions policy forbids it. Answering "no" costs a button nobody could have pressed;
 * answering "yes" wrongly costs a line and shows a notice saying VR is unavailable.
 */
async function immersiveAvailable(): Promise<boolean> {
  const xr = navigator.xr;
  if (!xr) return false;

  try {
    return await xr.isSessionSupported('immersive-vr');
  } catch {
    return false;
  }
}

/**
 * The colour the stage is painted, read from the stylesheet rather than repeated here.
 *
 * The canvas sits inside the stage and has to be indistinguishable from it, which two
 * copies of the same colour in two files would eventually stop being. Custom properties
 * come back as they were written, so this is the literal token — no colour parsing on
 * this side.
 */
function stageBackground(): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue('--surface-sunken')
    .trim();

  // Only empty if the stylesheet failed to load, in which case the page has bigger
  // problems; a colour is still needed to avoid handing three.js an empty string.
  return value === '' ? '#0e1114' : value;
}

start().catch((error: unknown) => {
  console.error(error);

  // Guarded, unlike every other use of the board: getting here means the application did
  // not start, so the markup it expects may be as broken as everything else. A failure to
  // report a failure would replace a message with a blank page.
  try {
    new NoticeBoard().show({
      level: 'error',
      title: 'The viewer failed to start',
      message: 'Your browser may not support WebGL or IndexedDB.',
      detail: 'The console holds the underlying error.',
    });
  } catch {
    document.body.textContent =
      'The viewer failed to start. Your browser may not support WebGL or IndexedDB.';
  }
});
